import { Injectable, inject } from '@angular/core';
import { LoggerService } from '../logger/logger.service';
import { TelemetryVdiBridgeService } from './telemetry-vdi-bridge.service';
import { TelemetrySyncService } from './telemetry-sync.service';
import { TelemetryEventStoreService } from './telemetry-event-store.service';
import { environment } from '../../../environments/environment';

const TAG = 'TelemetryLifecycle';

/**
 * Coordinates the start/stop of telemetry capture and the sync scheduler
 * around the VDI session lifecycle.
 *
 * Called by DashboardComponent:
 *  - `startSession(sessionId, hyLiteOrigin, iframeWindow)` — on successful VDI launch.
 *  - `stopSession()` — on VDI disconnect.
 *
 * Also resumes the sync after an authentication state change resolves a
 * prior 401 pause.
 */
@Injectable({ providedIn: 'root' })
export class TelemetryLifecycleService {
  private bridge = inject(TelemetryVdiBridgeService);
  private sync = inject(TelemetrySyncService);
  private store = inject(TelemetryEventStoreService);
  private logger = inject(LoggerService);

  /** Returns true if telemetry is enabled in the current environment. */
  private isTelemetryEnabled(): boolean {
    return environment.telemetry?.enabled ?? true;
  }

  /** Returns true if VDI click events are enabled in the current environment. */
  private isClickEventsEnabled(): boolean {
    return (environment as unknown as { vdiClickEvents?: { enabled?: boolean } }).vdiClickEvents?.enabled ?? true;
  }

  /**
   * Call once at application start after authentication hydration.
   * If unsynced records exist from a prior session, starts the single periodic sync
   * and drains them in the background. Otherwise stays IDLE.
   */
  async initOnBoot(): Promise<void> {
    if (!this.isTelemetryEnabled()) {
      this.logger.info(TAG, 'telemetry disabled by environment configuration');
      return;
    }

    // Run retention cleanup in the background — non-blocking.
    void this.store.runRetentionCleanup();

    // Check if any unsynced entries exist from a previous session
    const pendingCount = await this.store.getPendingCount();
    if (pendingCount > 0) {
      this.logger.info(TAG, `boot: found ${pendingCount} unsynced record(s) — starting periodic sync`);
      this.sync.startPeriodicSync();
      void this.sync.drain(true);
    } else {
      this.logger.info(TAG, 'boot: 0 unsynced records — sync engine IDLE');
    }
  }

  /**
   * Start capturing events for the given VDI session.
   *
   * @param sessionId — the PWA-generated VDI session ID (from `generateVdiSessionId`).
   * @param hyLiteOrigin — the resolved HyLite origin used to validate incoming postMessages.
   * @param iframeWindow — the active HyLite iframe's `contentWindow`; pass `null` when unavailable.
   */
  startSession(sessionId: string, hyLiteOrigin: string, iframeWindow: Window | null): void {
    const telemetryEnabled = this.isTelemetryEnabled();
    const clickEventsEnabled = this.isClickEventsEnabled();

    if (!telemetryEnabled && !clickEventsEnabled) return;

    this.logger.info(TAG, 'VDI session started', { sessionId, hyLiteOrigin });
    this.bridge.startCapture(sessionId, hyLiteOrigin, iframeWindow);

    if (telemetryEnabled) {
      this.sync.setVdiSessionActive(true);

      // If syncWhileVdiActive is enabled, start the single periodic sync process
      const syncWhileActive = environment.telemetry?.syncWhileVdiActive ?? true;
      if (syncWhileActive) {
        this.sync.startPeriodicSync();
      } else {
        // A recovery timer may have started at boot; real-time VDI mode owns sync per event.
        this.sync.stopPeriodicSync();
      }
    }
  }

  /**
   * Stop capturing events for the current VDI session.
   * Flushes any remaining pending records immediately and stops the timer once cleared.
   */
  stopSession(): void {
    const telemetryEnabled = this.isTelemetryEnabled();
    const clickEventsEnabled = this.isClickEventsEnabled();

    if (!telemetryEnabled && !clickEventsEnabled) return;

    this.logger.info(TAG, 'VDI session stopped');
    this.bridge.stopCapture();

    if (telemetryEnabled) {
      this.logger.info(TAG, 'flushing telemetry queue');
      this.sync.setVdiSessionActive(false);

      // Resume recovery polling after VDI ends and immediately retry all queued records.
      this.sync.startPeriodicSync();
      void this.sync.drain(true);
    }
  }

  /**
   * Resume sync after an authentication state change that resolved a prior 401.
   */
  onAuthRestored(): void {
    if (!this.isTelemetryEnabled()) return;

    this.logger.info(TAG, 'auth restored — resuming telemetry sync');
    this.sync.resumeAfterAuth();
  }
}
